import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
  getPoolsForDoctor,
  addDoctorToPool,
  updatePriority,
  changeDoctorActivity,
  removeDoctor,
} from '../../services/api/pool-manager';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';
import ConfirmModal from './ConfirmModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const PoolsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 14px;
`;

const PoolRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 14px;
  margin-top: 8px;
  background: ${(props) => (props.selected ? '#ede7fa' : '#fbfafe')};
  border: 1px solid ${(props) => (props.selected ? '#c9b8ee' : '#eeecf6')};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.12s ease;
  :hover {
    background: #f4f1fc;
  }
`;

const PoolMeta = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubLine = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-top: 2px;
`;

const ToggleButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  padding: 5px 12px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  color: #ffffff;
  min-width: 5.5rem;
  background: ${(props) => (props.active ? '#2fa060' : '#b0aabf')};
  transition: opacity 0.15s ease;
  :hover {
    opacity: 0.88;
  }
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid #e2b6b3;
  cursor: pointer;
  color: #a1170f;
  background: #fdf2f1;
  transition: background 0.15s ease;
  :hover {
    background: #fbe3e1;
  }
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const GhostButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.75rem;
  padding: 6px 12px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #f7f4fd;
  cursor: pointer;
  transition: background 0.15s ease;
  :hover {
    background: #efe9fb;
  }
`;

const BulkBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f3eefc;
  border: 1px solid #d9c9f4;
  border-radius: 10px;
`;

const BulkActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const BulkButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.75rem;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.danger ? '#e2b6b3' : '#d9d2ef')};
  color: ${(props) => (props.danger ? '#a1170f' : '#4b2e83')};
  background: ${(props) => (props.danger ? '#fdf2f1' : '#ffffff')};
  cursor: pointer;
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PoolLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  min-width: 0;
  gap: 10px;
`;

const PoolRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const Checkbox = styled.input`
  width: 17px;
  height: 17px;
  margin-top: 3px;
  cursor: pointer;
  accent-color: #4b2e83;
`;

const AddRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1eff7;
`;

const Select = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 9px 12px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.85rem;
  flex: 1;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const AddButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.85rem;
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  cursor: pointer;
  :disabled {
    background: #c5c0d4;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 12px;
`;

const PriorityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const PriorityLabel = styled(RegularText)`
  color: #7a7391;
  font-size: 0.78rem;
`;

const PriorityInput = styled.input`
  width: 4rem;
  padding: 4px 8px;
  border: 1px solid #ddd6ee;
  border-radius: 8px;
  font-size: 0.8rem;
  font-family: BrandonText-Medium, Lato, sans-serif;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const SavePriorityBtn = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.72rem;
  padding: 4px 10px;
  border: 1px solid #d9d2ef;
  border-radius: 8px;
  color: #4b2e83;
  background: #f7f4fd;
  cursor: pointer;
  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const isActive = (pool) => pool.active === 1 || pool.active === true;

class DoctorPoolsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pools: [],
      loading: false,
      error: null,
      addPoolId: '',
      adding: false,
      priorityEdits: {},
      savingPriorityId: null,
      togglingId: null,
      selectedPoolIds: [],
      confirmRemove: null,
      removing: false,
      bulkAction: null,
      bulkInFlight: false,
    };
  }

  componentDidMount() {
    this.getPools();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.doctorId !== this.props.doctorId) {
      this.setState({ selectedPoolIds: [] });
      this.getPools();
    }
  }

  getPools = () => {
    const { doctorId } = this.props;
    if (!doctorId) {
      return;
    }
    this.setState({ loading: true, error: null });
    getPoolsForDoctor(doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ pools: res.pools || [] });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to fetch pools');
        }
      })
      .catch((err) => {
        const message = err.message || 'Failed to fetch pools';
        toast.error(message);
        this.setState({ pools: [], error: message });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onAddToPool = () => {
    const { doctorId } = this.props;
    const { addPoolId } = this.state;
    if (!addPoolId) {
      toast.error('Choose a pool to add this doctor to.');
      return;
    }
    this.setState({ adding: true });
    addDoctorToPool(addPoolId, doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Doctor added to the pool.');
          this.setState({ addPoolId: '' });
          this.getPools();
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to add to pool');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to add to pool');
      })
      .finally(() => {
        this.setState({ adding: false });
      });
  };

  getPriorityValue = (pool) => {
    const { priorityEdits } = this.state;
    const key = `${pool.poolId}`;
    if (Object.prototype.hasOwnProperty.call(priorityEdits, key)) {
      return priorityEdits[key];
    }
    return pool.priority !== undefined && pool.priority !== null
      ? pool.priority
      : 0;
  };

  onPriorityChange = (poolId, value) => {
    this.setState((prev) => ({
      priorityEdits: { ...prev.priorityEdits, [`${poolId}`]: value },
    }));
  };

  onToggleActive = (pool, e) => {
    if (e) {
      e.stopPropagation();
    }
    const { doctorId } = this.props;
    const nextActive = !isActive(pool);
    this.setState({ togglingId: pool.poolId });
    changeDoctorActivity(pool.poolId, doctorId, nextActive)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(
            `Marked ${nextActive ? 'active' : 'inactive'} in ${
              pool.poolName || 'pool'
            }.`
          );
          this.setState((prev) => ({
            pools: prev.pools.map((p) =>
              `${p.poolId}` === `${pool.poolId}`
                ? { ...p, active: nextActive ? 1 : 0 }
                : p
            ),
          }));
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update status'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update status');
      })
      .finally(() => {
        this.setState({ togglingId: null });
      });
  };

  onSavePriority = (pool) => {
    const { doctorId } = this.props;
    const value = parseInt(this.getPriorityValue(pool), 10);
    if (Number.isNaN(value)) {
      toast.error('Enter a valid priority.');
      return;
    }
    this.setState({ savingPriorityId: pool.poolId });
    updatePriority(pool.poolId, doctorId, value, [], null)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Priority updated.');
          this.setState((prev) => {
            const edits = { ...prev.priorityEdits };
            delete edits[`${pool.poolId}`];
            return {
              priorityEdits: edits,
              pools: prev.pools.map((p) =>
                `${p.poolId}` === `${pool.poolId}`
                  ? { ...p, priority: value }
                  : p
              ),
            };
          });
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to update priority'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to update priority');
      })
      .finally(() => {
        this.setState({ savingPriorityId: null });
      });
  };

  isChecked = (poolId) => {
    const { selectedPoolIds } = this.state;
    return selectedPoolIds.some((id) => `${id}` === `${poolId}`);
  };

  onToggleSelection = (poolId, e) => {
    if (e) {
      e.stopPropagation();
    }
    this.setState((prev) => {
      const current = prev.selectedPoolIds;
      const next = current.some((id) => `${id}` === `${poolId}`)
        ? current.filter((id) => `${id}` !== `${poolId}`)
        : [...current, poolId];
      return { selectedPoolIds: next };
    });
  };

  onToggleAll = () => {
    this.setState((prev) => {
      const allIds = prev.pools.map((p) => p.poolId);
      const allSelected =
        allIds.length > 0 &&
        allIds.every((id) =>
          prev.selectedPoolIds.some((s) => `${s}` === `${id}`)
        );
      return { selectedPoolIds: allSelected ? [] : allIds };
    });
  };

  onConfirmRemove = () => {
    const { doctorId } = this.props;
    const { confirmRemove } = this.state;
    if (!confirmRemove) {
      return;
    }
    this.setState({ removing: true });
    removeDoctor(confirmRemove.poolId, doctorId)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success(
            `Removed from ${confirmRemove.poolName || 'pool'}.`
          );
          this.setState((prev) => ({
            confirmRemove: null,
            selectedPoolIds: prev.selectedPoolIds.filter(
              (id) => `${id}` !== `${confirmRemove.poolId}`
            ),
          }));
          this.getPools();
        } else {
          throw new Error(
            (res && res.errorMessage) || 'Failed to remove from pool'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to remove from pool');
      })
      .finally(() => {
        this.setState({ removing: false });
      });
  };

  runBulk = () => {
    const { doctorId } = this.props;
    const { bulkAction, selectedPoolIds, pools } = this.state;
    const targets = pools.filter((p) =>
      selectedPoolIds.some((id) => `${id}` === `${p.poolId}`)
    );
    if (!targets.length || !bulkAction) {
      this.setState({ bulkAction: null });
      return;
    }
    this.setState({ bulkInFlight: true });
    const promises = targets.map((pool) => {
      const call =
        bulkAction === 'deactivate'
          ? changeDoctorActivity(pool.poolId, doctorId, false)
          : removeDoctor(pool.poolId, doctorId);
      return call
        .then((res) => ({ pool, ok: res && res.message === 'success', res }))
        .catch((err) => ({ pool, ok: false, err }));
    });
    Promise.all(promises)
      .then((results) => {
        const ok = results.filter((r) => r.ok).length;
        const failed = results.length - ok;
        const verb = bulkAction === 'deactivate' ? 'deactivated' : 'removed';
        if (ok) {
          toast.success(`${ok} pool${ok !== 1 ? 's' : ''} ${verb}.`);
        }
        if (failed) {
          toast.error(`${failed} failed. Refreshing…`);
        }
      })
      .finally(() => {
        this.setState({
          bulkAction: null,
          bulkInFlight: false,
          selectedPoolIds: [],
        });
        this.getPools();
      });
  };

  render() {
    const {
      pools,
      loading,
      error,
      addPoolId,
      adding,
      savingPriorityId,
      togglingId,
      selectedPoolIds,
      confirmRemove,
      removing,
      bulkAction,
      bulkInFlight,
    } = this.state;
    const { doctorName, selectedPoolId, onSelectPool, allPools } = this.props;

    const memberPoolIds = new Set(pools.map((p) => `${p.poolId}`));
    const availablePools = (allPools || []).filter(
      (p) => !memberPoolIds.has(`${p.poolId}`)
    );
    const allSelected =
      pools.length > 0 &&
      pools.every((p) =>
        selectedPoolIds.some((id) => `${id}` === `${p.poolId}`)
      );
    const bulkVerb = bulkAction === 'deactivate' ? 'deactivate' : 'remove';
    const bulkPastVerb = bulkAction === 'deactivate' ? 'deactivated' : 'removed';

    return (
      <Container>
        <HeaderRow>
          <BoldText fontSize="1.05rem">{`Pools · ${doctorName || 'Doctor'}`}</BoldText>
          {pools.length ? (
            <HeaderActions>
              <GhostButton onClick={this.onToggleAll}>
                {allSelected ? 'Clear all' : 'Select all'}
              </GhostButton>
            </HeaderActions>
          ) : null}
        </HeaderRow>
        {selectedPoolIds.length ? (
          <BulkBar>
            <RegularText>
              {`${selectedPoolIds.length} selected`}
            </RegularText>
            <BulkActions>
              <BulkButton
                disabled={bulkInFlight}
                onClick={() => this.setState({ bulkAction: 'deactivate' })}
              >
                Deactivate selected
              </BulkButton>
              <BulkButton
                danger
                disabled={bulkInFlight}
                onClick={() => this.setState({ bulkAction: 'remove' })}
              >
                Remove selected
              </BulkButton>
              <BulkButton
                disabled={bulkInFlight}
                onClick={() => this.setState({ selectedPoolIds: [] })}
              >
                Clear
              </BulkButton>
            </BulkActions>
          </BulkBar>
        ) : null}
        {loading ? (
          <LoadingComponent />
        ) : error ? (
          <HelperText>{error}</HelperText>
        ) : (
          <React.Fragment>
            <PoolsContainer>
              {pools.map((pool) => (
                <PoolRow
                  key={pool.poolId}
                  selected={
                    selectedPoolId && `${selectedPoolId}` === `${pool.poolId}`
                  }
                  onClick={() => onSelectPool(pool)}
                >
                  <PoolLeft>
                    <Checkbox
                      type="checkbox"
                      checked={this.isChecked(pool.poolId)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => this.onToggleSelection(pool.poolId, e)}
                    />
                    <PoolMeta>
                      <BoldText>{pool.poolName}</BoldText>
                      <SubLine>{`Pool ${pool.poolId}`}</SubLine>
                      <PriorityRow onClick={(e) => e.stopPropagation()}>
                        <PriorityLabel>Priority</PriorityLabel>
                        <PriorityInput
                          type="number"
                          value={this.getPriorityValue(pool)}
                          onChange={(e) =>
                            this.onPriorityChange(pool.poolId, e.target.value)
                          }
                        />
                        <SavePriorityBtn
                          disabled={`${savingPriorityId}` === `${pool.poolId}`}
                          onClick={() => this.onSavePriority(pool)}
                        >
                          {`${savingPriorityId}` === `${pool.poolId}`
                            ? '…'
                            : 'Save'}
                        </SavePriorityBtn>
                      </PriorityRow>
                    </PoolMeta>
                  </PoolLeft>
                  <PoolRight>
                    <ToggleButton
                      active={isActive(pool)}
                      disabled={`${togglingId}` === `${pool.poolId}`}
                      onClick={(e) => this.onToggleActive(pool, e)}
                    >
                      {`${togglingId}` === `${pool.poolId}`
                        ? '…'
                        : isActive(pool)
                        ? 'Active'
                        : 'Inactive'}
                    </ToggleButton>
                    <RemoveButton
                      onClick={(e) => {
                        e.stopPropagation();
                        this.setState({ confirmRemove: pool });
                      }}
                    >
                      Remove
                    </RemoveButton>
                  </PoolRight>
                </PoolRow>
              ))}
              {!pools.length ? (
                <HelperText>Not in any pool yet.</HelperText>
              ) : null}
            </PoolsContainer>

            {availablePools.length ? (
              <AddRow>
                <Select
                  value={addPoolId}
                  onChange={(e) => this.setState({ addPoolId: e.target.value })}
                >
                  <option value="">Add to a pool…</option>
                  {availablePools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
                <AddButton disabled={adding || !addPoolId} onClick={this.onAddToPool}>
                  {adding ? 'Adding…' : 'Add'}
                </AddButton>
              </AddRow>
            ) : null}
          </React.Fragment>
        )}

        <ConfirmModal
          isOpen={!!confirmRemove}
          title="Remove from pool"
          danger
          loading={removing}
          confirmText="Remove"
          message={
            confirmRemove
              ? `Remove ${doctorName || 'this doctor'} from ${
                  confirmRemove.poolName || `pool ${confirmRemove.poolId}`
                }?`
              : ''
          }
          onConfirm={this.onConfirmRemove}
          onCancel={() => this.setState({ confirmRemove: null })}
        />

        <ConfirmModal
          isOpen={!!bulkAction}
          title={bulkAction === 'deactivate' ? 'Deactivate in selected pools' : 'Remove from selected pools'}
          danger={bulkAction === 'remove'}
          loading={bulkInFlight}
          confirmText={bulkAction === 'deactivate' ? 'Deactivate' : 'Remove'}
          message={`${bulkVerb.charAt(0).toUpperCase() + bulkVerb.slice(1)} ${
            doctorName || 'this doctor'
          } in ${selectedPoolIds.length} selected pool${
            selectedPoolIds.length !== 1 ? 's' : ''
          }? Doctor will be ${bulkPastVerb} from each.`}
          onConfirm={this.runBulk}
          onCancel={() => this.setState({ bulkAction: null })}
        />
      </Container>
    );
  }
}

export default DoctorPoolsList;
